// Logger utility for development mode
const isDevelopment = import.meta.env.DEV

export type LogLevel = 'error' | 'warn' | 'info' | 'debug'
type LogContext = unknown

export const LOG_LEVELS: Record<Uppercase<LogLevel>, LogLevel> = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
}

class Logger {
  private isDev: boolean

  constructor() {
    this.isDev = isDevelopment
  }

  private formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString()
    return `[${timestamp}] [${level.toUpperCase()}] ${message}`
  }

  private log(level: LogLevel, message: string, ...args: LogContext[]): void {
    if (!this.isDev && level !== 'error' && level !== 'warn') return

    const formattedMessage = this.formatMessage(level, message)

    switch (level) {
      case 'error':
        console.error(formattedMessage, ...args)
        break
      case 'warn':
        console.warn(formattedMessage, ...args)
        break
      case 'info':
        console.info(formattedMessage, ...args)
        break
      case 'debug':
        console.debug(formattedMessage, ...args)
        break
      default:
        console.log(formattedMessage, ...args)
    }
  }

  error(message: string, ...args: LogContext[]): void {
    this.log('error', message, ...args)
  }

  warn(message: string, ...args: LogContext[]): void {
    this.log('warn', message, ...args)
  }

  info(message: string, ...args: LogContext[]): void {
    this.log('info', message, ...args)
  }

  debug(message: string, ...args: LogContext[]): void {
    this.log('debug', message, ...args)
  }

  // API request logging
  apiRequest(method: string, url: string, data?: LogContext): void {
    this.info(`🚀 API Request: ${method.toUpperCase()} ${url}`, data ? { body: data } : '')
  }

  apiResponse(method: string, url: string, status: number, data?: LogContext): void {
    const statusEmoji = status >= 200 && status < 300 ? '✅' : status >= 400 ? '❌' : '⚠️'
    this.info(`${statusEmoji} API Response: ${method.toUpperCase()} ${url} [${status}]`, data || '')
  }

  apiError(method: string, url: string, error: LogContext): void {
    this.error(`💥 API Error: ${method.toUpperCase()} ${url}`, error)
  }

  // Authentication logging
  authLogin(username: string): void {
    this.info(`🔐 Login attempt for user: ${username}`)
  }

  authLoginSuccess(username: string): void {
    this.info(`✅ Login successful for user: ${username}`)
  }

  authLoginError(username: string, error: LogContext): void {
    this.error(`❌ Login failed for user: ${username}`, error)
  }

  authLogout(): void {
    this.info(`👋 User logged out`)
  }

  authTokenRefresh(): void {
    this.info(`🔄 Token refresh attempted`)
  }

  authTokenRefreshSuccess(): void {
    this.info(`✅ Token refresh successful`)
  }

  authTokenRefreshError(error: LogContext): void {
    this.error(`❌ Token refresh failed`, error)
  }

  // Navigation logging
  navigationTo(route: string): void {
    this.debug(`🧭 Navigation to: ${route}`)
  }

  // Component lifecycle logging
  componentMounted(componentName: string): void {
    this.debug(`🔧 Component mounted: ${componentName}`)
  }

  componentUnmounted(componentName: string): void {
    this.debug(`🗑️ Component unmounted: ${componentName}`)
  }
}

// Export singleton instance
export const logger = new Logger()
export default logger
